import React, { useEffect, useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { useParams } from 'react-router-dom';
import { UnknownObject } from '../../../../react-app-env';
import moment from 'moment';

import { useQuery } from '@apollo/client';

import { Box, Button, Grid, Typography } from '@mui/material';

import GeneralLayout from '../../../../layouts/General';
import ReadOnlyBlock from '../../../../components/ReadOnlyBlock';
import SpecialityCard from '../../../../components/SpecialityCard';
import PotQR from '../../../../components/QrCodes/PotQR';

import { Case, PathwayVersion, Pot, selectCaseInitQuery } from './queries';

import styles from './styles';
import { Download } from '@mui/icons-material';
import { Document } from '../../../../utils/types';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
}

const ViewCase = ({ classes }: Props): React.ReactElement => {
  const { case_id } = useParams();

  const [caseData, setCase] = useState<Case | UnknownObject>({});

  const { data: caseInitData } = useQuery(selectCaseInitQuery(case_id!), { fetchPolicy: 'no-cache' });

  useEffect(() => {
    let mounted = true;
    if (mounted && caseInitData) {
      setCase(caseInitData.cases_by_pk);
    }
    return () => {
      mounted = false;
    };
  }, [caseInitData]);

  const getPatientInfoFromEvents = (caseData: Case | UnknownObject) => {
    if (caseData?.case_events?.length) {
      const caseDataEvent = caseData.case_events[0];

      return `${caseDataEvent.event_data?.patient.first_name} ${caseDataEvent.event_data?.patient.last_name}`;
    }

    return '';
  };

  const downloadFile = (fileId: string) => {
    window.open(`${process.env.REACT_APP_AWS_PDF_REPORTS_URL}/${fileId}`, '_blank');
  };

  const patientName = getPatientInfoFromEvents(caseData);
  const patientNhsNumber = caseData?.patient?.nhs_id || '';
  const patientDob = moment(caseData?.patient?.dob).format('DD/MM/yyyy');
  const shouldShowResults = !!caseData?.reports_text || !!caseData?.reports_file_id;
  return (
    <GeneralLayout breadcrumbs={[{ label: 'Cases', link: '/org/cases' }, { label: caseData.accession_id }]}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <ReadOnlyBlock
                fill
                title="Patient Details"
                items={[
                  { label: 'Name', value: patientName },
                  { label: 'NHS number', value: patientNhsNumber },
                  { label: 'Medical Record Number', value: caseData?.mrn },
                  { label: 'Date of Birth', value: patientDob },
                ]}
                components={[
                  <>
                    {!!caseData?.documents?.length && (
                      <Box>
                        <Typography variant="subtitle1" color="textSecondary">
                          Document(s)
                        </Typography>
                        {caseData?.documents?.map((doc: Document) => (
                          <Button
                            key={doc.id}
                            variant="text"
                            sx={{ textTransform: 'none' }}
                            color="primary"
                            startIcon={<Download />}
                            onClick={() => downloadFile(`${doc.id}/${encodeURIComponent(doc.document_name)}`)}>
                            {doc.document_name}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </>,
                ]}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyBlock
                fill
                title="Sender Details"
                items={[
                  { label: 'Organisation', value: caseData?.organisation?.name },
                  { label: 'Lab', value: caseData?.lab?.name },
                  { label: 'Requester', value: caseData?.created_by_user?.full_name },
                ]}
              />
            </Grid>
            <Grid item xs={4}>
              <ReadOnlyBlock
                fill
                center
                components={[
                  <PotQR
                    patientInformation={{
                      name: patientName,
                      nhsNumber: patientNhsNumber,
                      dob: patientDob,
                    }}
                    rawData={caseData.id}
                  />,
                ]}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={shouldShowResults ? 4 : 6}>
              <ReadOnlyBlock
                fill
                title="Clinical Details"
                components={caseData?.surgical_pathway_versions?.map((version: PathwayVersion) => (
                  <div className={classes.reviewDetailsContainer}>
                    <SpecialityCard small divider gridSize={12} title={version.category.speciality.name} description={version.category.name} />
                    <div className={classes.specialityDetails}>
                      <Grid container spacing={2}>
                        {version?.surgical_pathways?.map((pathway, idx) => {
                          const isFreetext = pathway.component_type === 'FREETEXT';
                          return (
                            <Grid key={`pathway-${idx}`} item xs={isFreetext ? 12 : 6}>
                              <div className={classes.specialityStep}>
                                <Typography variant="subtitle2" color="textSecondary">
                                  {pathway.title}
                                </Typography>
                                <Typography className={isFreetext ? classes.stepValuePreserveWhitespace : classes.stepValue} variant="body1">
                                  {pathway.label}
                                </Typography>
                              </div>
                            </Grid>
                          );
                        })}
                        {version.note && (
                          <Grid item xs={12}>
                            <Typography variant="body1" color="textSecondary">
                              Additional information: {version.note}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  </div>
                ))}
              />
            </Grid>
            <Grid item xs={shouldShowResults ? 4 : 6}>
              <ReadOnlyBlock
                fill
                title="Samples"
                components={[
                  <div className={classes.reviewDetailsContainer}>
                    {caseData?.surgical_pathway_versions?.map((version: PathwayVersion) => {
                      const pots = caseData.pots.filter((i: Pot) => i.speciality_category_id === version.category.id) as Pot[];

                      return (
                        <Box>
                          <SpecialityCard small divider gridSize={12} title={version.category.speciality.name} description={version.category.name} />
                          {pots.map((pot: Pot) => (
                            <div key={pot.id} className={classes.specialityDetails}>
                              <Typography variant="subtitle1" color="textSecondary">
                                {pot.name}
                              </Typography>
                              <Typography className={classes.stepValue} variant="body1">
                                {pot.pathway.map((i: UnknownObject) => i.label).join(', ')}
                              </Typography>
                              {pot.note && (
                                <Typography className={classes.stepValue} variant="subtitle2" color="textSecondary">
                                  Additional information: {pot.note}
                                </Typography>
                              )}
                            </div>
                          ))}
                        </Box>
                      );
                    })}
                  </div>,
                ]}
              />
            </Grid>
            {shouldShowResults && (
              <Grid item xs={4}>
                <ReadOnlyBlock
                  fill
                  title="Results"
                  components={[
                    <div className={classes.reviewDetailsContainer}>
                      {caseData?.reports_text && (
                        <div className={classes.reportContainer}>
                          <Typography variant="subtitle1" color="textSecondary">
                            Results Text report
                          </Typography>
                          <Typography className={classes.reportValue} variant="body2">
                            {caseData.reports_text}
                          </Typography>
                        </div>
                      )}
                      {caseData?.reports_file_id && (
                        <div className={classes.reportContainer}>
                          <Typography variant="subtitle1" color="textSecondary">
                            Results File report
                          </Typography>
                          <Button onClick={() => downloadFile(caseData.reports_file_id)} variant="contained" startIcon={<Download />}>
                            Download PDF
                          </Button>
                        </div>
                      )}
                    </div>,
                  ]}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </GeneralLayout>
  );
};

export default withStyles(styles)(ViewCase);
